import React, { useState, useCallback } from 'react';
import classNames from 'classnames';
import Container from 'reactstrap/lib/Container';
import { Button } from 'reactstrap/lib';
import { isEmpty } from 'lodash';
import { DealerDetailsLogo } from 'site-modules/dealerships/components/dealer-details-logo/dealer-details-logo';
import { DealerInfoEntities } from 'client/data/models/dealer-info';
import { weekdays } from 'client/utils/weekdays';
import { getGoogleMapsUrl } from 'site-modules/shared/utils/dealer-details-utils';
import { DealerWorkTimeStatus } from 'site-modules/dealerships/components/dealer-details-work-hours/dealer-working-time-status';
import { Collapse } from 'site-modules/shared/components/collapse/collapse';

import './dealer-details.scss';

const WORK_HOURS_LIST_ID = 'dd-work-hours-list';

export function DealerDetails({ dealerInfo }) {
  const [workHoursAreOpen, setWorkHoursAreOpen] = useState(false);
  const { logoUrl, workHours, address } = dealerInfo;
  const date = new Date();
  const currentDay = weekdays[date.getDay()];

  const toggleWorkHoursRanges = useCallback(() => {
    setWorkHoursAreOpen(!workHoursAreOpen);
  }, [workHoursAreOpen]);

  /**
   * Get day work hours range string, e.g. 09:00 AM — 09:00 PM
   * @param dayName string
   */
  const getCurrentDayRange = useCallback(
    dayName => {
      const day = workHours?.[dayName.toLowerCase()];
      if (!day) return 'closed';
      return (
        <span>
          <span className="text-nowrap">{day.open}</span> - <span className="text-nowrap">{day.close}</span>
        </span>
      );
    },
    [workHours]
  );

  return (
    <Container className="dealer-details px-1">
      <div className="rounded-16 bg-cool-gray-90 px-1 px-lg-1_5 py-1">
        <div className="d-flex flex-column flex-lg-row align-items-lg-center">
          {logoUrl && (
            <div className="me-lg-1_5 mb-2 mb-lg-0">
              <DealerDetailsLogo logoUrl={logoUrl} />
            </div>
          )}
          {!!address && (
            <div className="me-lg-1_5">
              <a
                className="d-inline-block"
                href={getGoogleMapsUrl(address)}
                rel="noopener noreferrer nofollow"
                data-tracking-id="ddp_view_dealer_hours_and_directions"
                target="_blank"
                data-testid="dealer-address"
              >
                <span className="icon-location text-cool-gray-40 me-0_5" aria-hidden />
                <span className="text-underline">
                  {address.street}, {address.city}, {address.stateCode}&nbsp;{address.zip}
                </span>
              </a>
            </div>
          )}
          {!isEmpty(workHours) && (
            <div className="dealer-details-work-hours d-flex flex-column flex-lg-row justify-content-lg-between border-left w-100 col-lg p-0 ps-lg-1_5 mt-1 mt-lg-0">
              <div className="d-flex align-items-baseline">
                <span className="icon-clock me-0_5" aria-hidden />
                <div>
                  <span className="me-0_5">Today {getCurrentDayRange(currentDay)}</span>
                  <DealerWorkTimeStatus
                    currentDay={currentDay}
                    workHours={workHours}
                    dealerZip={address?.zip}
                    className="me-1 d-inline-block"
                  />
                </div>
              </div>
              <div className="mt-0_5 mt-lg-0">
                <Button
                  color="link"
                  className="px-0 py-0 text-transform-none fw-normal"
                  onClick={toggleWorkHoursRanges}
                  data-tracking-id="ddp_view_dealer_hours"
                  aria-expanded={workHoursAreOpen}
                  aria-controls={WORK_HOURS_LIST_ID}
                >
                  <span className="text-underline size-16">Show all hours</span>
                  <span
                    className={classNames(
                      { 'icon-arrow-up3': workHoursAreOpen, 'icon-arrow-down3': !workHoursAreOpen },
                      'ms-0_25 size-12'
                    )}
                    aria-hidden
                  />
                </Button>
              </div>
            </div>
          )}
        </div>
        {!isEmpty(workHours) && (
          <Collapse
            id={WORK_HOURS_LIST_ID}
            isOpen={workHoursAreOpen}
            className={classNames({ 'pt-0_5': workHoursAreOpen })}
            transition="0.25s ease-in-out"
          >
            {weekdays.map(dayName => (
              <div key={dayName} className="d-flex pt-1_5">
                <div className="work-day-name text-capitalize fw-bold me-2_5">{dayName}</div>
                <div>{getCurrentDayRange(dayName)}</div>
              </div>
            ))}
          </Collapse>
        )}
      </div>
    </Container>
  );
}

DealerDetails.propTypes = {
  dealerInfo: DealerInfoEntities.DealerInfo,
};
DealerDetails.defaultProps = {
  dealerInfo: {},
};
